
import { Component, Mixins } from 'vue-property-decorator';
import PageHeader from '@/components/layout/PageHeader.vue';
import HasScope from '@/components/Mixins/HasScope';
import { Roles } from '@/services/Roles';
import { ProfileRepositoryInterface } from '@/services/Users-profile/interfaces';

interface OverviewItem {
  title: string;
  description: string;
  icon: string;
  to: string;
  scope: string;
}

interface OverviewSection {
  id: string;
  title: string;
  items: OverviewItem[];
}

@Component({
  components: { PageHeader },
  inject: ['profileRepository'],
})
export default class DashboardDashboard extends Mixins(HasScope) {
  private readonly profileRepository!: ProfileRepositoryInterface;

  private readonly sections: OverviewSection[] = [
    {
      id: 'hosting',
      title: 'Hosting',
      items: [
        {
          title: 'Websites',
          description: 'Search and manage domains, status, staging, and related resources.',
          icon: 'mdi-cloud',
          to: '/websites',
          scope: Roles.WEBSITE_WEBSITE_READ,
        },
        {
          title: 'Website groups',
          description: 'Group sites under an owner and plan; useful for multi-site accounts.',
          icon: 'mdi-cloud-print',
          to: '/websites/groups',
          scope: Roles.WEBSITE_WEBSITE_READ,
        },
        {
          title: 'Orchestrator',
          description: 'Follow create/delete pipelines and per-action status for websites.',
          icon: 'mdi-sitemap',
          to: '/website-orchestrator',
          scope: Roles.WEBSITE_WEBSITE_READ,
        },
        {
          title: 'Migrations',
          description: 'Track website, group, and database migrations between servers.',
          icon: 'mdi-cloud-arrow-right',
          to: '/website-migrations',
          scope: Roles.WEBSITE_MIGRATIONS_READ,
        },
      ],
    },
    {
      id: 'infrastructure',
      title: 'Infrastructure',
      items: [
        {
          title: 'Servers',
          description: 'List origin/edge servers and open server detail for ops tasks.',
          icon: 'mdi-server',
          to: '/server',
          scope: Roles.SERVER_SERVER_READ,
        },
        {
          title: 'DNS zones',
          description: 'Manage zones and records; start here for DNS lookups and edits.',
          icon: 'mdi-routes',
          to: '/dns',
          scope: Roles.DNS_ZONE_READ,
        },
        {
          title: 'SSL certificates',
          description: 'View and issue certificates tied to websites and aliases.',
          icon: 'mdi-certificate-outline',
          to: '/ssl',
          scope: Roles.SSL_CERT_READ,
        },
        {
          title: 'CDN vhosts',
          description: 'CDN / edge vhost configuration for published sites.',
          icon: 'mdi-cloud-outline',
          to: '/vhost/cdn',
          scope: Roles.VHOST_VHOST_READ,
        },
        {
          title: 'Origin vhosts',
          description: 'Origin vhost, PHP, and alias settings on the hosting server.',
          icon: 'mdi-cloud-print',
          to: '/vhost/origin',
          scope: Roles.VHOST_VHOST_CREATE,
        },
      ],
    },
    {
      id: 'resources',
      title: 'Site resources',
      items: [
        {
          title: 'Databases',
          description: 'MySQL databases and DB users linked to websites.',
          icon: 'mdi-database',
          to: '/databases/list',
          scope: Roles.DATABASE_USER_READ,
        },
        {
          title: 'System users',
          description: 'Unix / system users that own site files on the server.',
          icon: 'mdi-account-cog',
          to: '/system-users/list',
          scope: Roles.SYSTEM_USER_READ,
        },
        {
          title: 'Mailboxes',
          description: 'Email mailboxes and related mailbox operations.',
          icon: 'mdi-mailbox-up-outline',
          to: '/mailboxes',
          scope: Roles.MAILBOX_MAILBOX_READ,
        },
        {
          title: 'FTP users',
          description: 'FTP accounts for file access on hosting servers.',
          icon: 'mdi-file-account-outline',
          to: '/ftp-users',
          scope: Roles.FTP_USER_FTP_USER_READ,
        },
        {
          title: 'Plans',
          description: 'Hosting plans attached to website groups.',
          icon: 'mdi-clipboard-text-outline',
          to: '/plans',
          scope: Roles.SYSTEM_USER_PLAN_READ,
        },
      ],
    },
    {
      id: 'ops',
      title: 'Ops & recovery',
      items: [
        {
          title: 'Backups',
          description: 'Backups, restores, schedules, and backup servers/repos.',
          icon: 'mdi-backup-restore',
          to: '/backups',
          scope: Roles.BACKUP_BACKUP_READ,
        },
        {
          title: 'IP sets',
          description: 'IP allow/deny sets used for access control.',
          icon: 'mdi-ip',
          to: '/ipset/sets',
          scope: Roles.IPSET_IPSET_READ,
        },
        {
          title: 'Usage',
          description: 'Resource usage and quota-related views.',
          icon: 'mdi-chart-bar',
          to: '/usage',
          scope: Roles.USAGE_READ,
        },
      ],
    },
    {
      id: 'access',
      title: 'People & access',
      items: [
        {
          title: 'Users',
          description: 'Console user accounts and authentication details.',
          icon: 'mdi-account',
          to: '/users',
          scope: Roles.USER_USERS_READ,
        },
        {
          title: 'Roles',
          description: 'Roles and permission scopes that gate sidebar items.',
          icon: 'mdi-shield-account',
          to: '/roles',
          scope: Roles.USER_ROLES_READ,
        },
        {
          title: 'Departments',
          description: 'Organize users into departments for access and routing.',
          icon: 'mdi-office-building',
          to: '/departments',
          scope: Roles.USER_DEPARTMENTS_READ,
        },
      ],
    },
    {
      id: 'support',
      title: 'Support & quality',
      items: [
        {
          title: 'QC',
          description: 'Chat/ticket review queues, feedback, escalations, and stats.',
          icon: 'mdi-text-box-check',
          to: '/qc/chats',
          scope: Roles.QA_CHAT_REVIEW,
        },
        {
          title: 'Agent profiles',
          description: 'Dossiers, teams, and agent feedback for support staff.',
          icon: 'mdi-account-tie-hat',
          to: '/dossiers/',
          scope: Roles.DOSSIER_USER_READ,
        },
        {
          title: 'Bug reports',
          description: 'Track and update internal bug reports.',
          icon: 'mdi-bug-outline',
          to: '/internal-tickets/bugs',
          scope: Roles.SMS_SMS_READ,
        },
        {
          title: 'Feature requests',
          description: 'Internal feature request tracking.',
          icon: 'mdi-lightbulb-outline',
          to: '/internal-tickets/features',
          scope: Roles.SMS_SMS_READ,
        },
      ],
    },
  ];

  public get visibleSections(): OverviewSection[] {
    return this.sections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => this.userHasPermission(item.scope)),
      }))
      .filter((section) => section.items.length > 0);
  }

  private async created(): Promise<void> {
    if (!this.$store.getters.id) {
      return;
    }

    try {
      const notifications = await this.profileRepository.getProfileNotifications();
      if (notifications) {
        this.$store.commit('SET_NOTIFICATIONS', notifications);
      }
    } catch (e) {
      console.log(e);
    }
  }
}
